import { useMediaQuery } from '@mantine/hooks';
import { useState } from 'react';
import {
  employedsApi,
  useAssignCategoryToWorkerMutation,
  useBlockCommentWorkerMutation,
  useBlockWorkerMutation,
  useUnblockWorkerMutation,
} from 'redux/Api/employedsApi/employedsApi';
import styles from './EmployedsProfileContent.module.scss';
import moment from 'moment';
import { FnsStatusWithIcon, WorkerAppStatusWithIcon } from 'components/WorkerStatus';
import { statusColor, statusIcons } from 'components/ContractsListMobile';
import { statusText } from 'components/ContractItem';
import { Switch } from '@mantine/core';
import { ModalConfirm } from 'components/ContractItem/ModalConfirm';
import { ModalComment } from 'components/ContractItem/ModalComment';
import { Button } from 'ui/Button';
import { enumerate } from 'utils/strActions';
import { ReactComponent as IconDone } from './img/iconDone.svg';
import { ReactComponent as IconEdit } from './img/iconEdit.svg';
import { ReactComponent as IconClose } from './img/iconClose.svg';
import { ReactComponent as IconPlus } from './img/iconPlus.svg';
import { ReactComponent as IconAlert } from './img/iconAlert.svg';
import { ContractStatus, ContractType } from 'redux/Api/contractsApi/type';
import { TextWithTooltip } from 'hooks/TextWithTooltip';
import { EmployedsСategory } from '../../components/EmployedsСategory';
import { EmployedsCategoryDrawer } from 'components/EmployedsСategory/EmployedsCategoryDrawer';
import { Employed } from 'redux/Api/employedsApi/type';
import { useDispatch, useStore } from 'react-redux';
import { updateEmployedCategories } from 'redux/employeds/slice';
import { RootState } from 'redux/store';
import { mergeSelectedCategoriesForStore } from 'utils/mergeSelectedCategoriesForStore';
import { useContractActions } from 'components/ContractItem/hooks/useContractActions';

export const StatusProfileContent = ({
  worker,
  refetch,
  setIsCreatedModalOpen,
}: {
  worker: Employed;
  refetch: () => void;
  setIsCreatedModalOpen: (e: boolean) => void;
}) => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const [openCategoryId, setOpenCategoryId] = useState<number | null>(null);
  const isMobile = useMediaQuery('(width < 768px)');
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showEditCommentModal, setShowEditCommentModal] = useState(false);
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState(false);
  const [blockWorker] = useBlockWorkerMutation();
  const [unblockWorker] = useUnblockWorkerMutation();
  const [blockCommentWorker] = useBlockCommentWorkerMutation();
  const [assignCategoryToWorker] = useAssignCategoryToWorkerMutation();

  const { confirmTerminateContract } = useContractActions(
    worker?.unified_contract as ContractType | null | undefined,
  );

  const birthDate = new Date(worker?.worker_app?.birthdate);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  const reformattedAge = String(worker?.worker_app?.birthdate)
    .split('-')
    .reverse()
    .join('.');

  const handleSwitchChange = () => {
    if (pendingSwitch) return;
    if (!worker.is_in_stoplist) {
      setShowCommentModal(true);
    } else {
      setPendingSwitch(true);
      unblockWorker({ workerId: worker.id })
        .then(() => {
          setPendingSwitch(false);
          refetch();
        })
        .catch(() => setPendingSwitch(false));
    }
  };

  const handleBlockWithComment = (comment: string) => {
    setPendingSwitch(true);
    blockWorker({ workerId: worker.id, comment })
      .then(() => {
        setShowCommentModal(false);
        setPendingSwitch(false);
        refetch();
      })
      .catch(() => setPendingSwitch(false));
  };

  const handleEditComment = (comment: string) => {
    blockCommentWorker({ workerId: worker.id, comment })
      .then(() => {
        setShowEditCommentModal(false);
        refetch();
      })
      .catch(() => setShowEditCommentModal(false));
  };

  const hasContract =
    worker?.unified_contract?.status === 'signed_by_worker' ||
    worker?.unified_contract?.status === 'signed_by_customer';
  const isResident = worker?.worker_app?.nationality === 'Россия';

  const handleCategoryToggle = (employedId: number) => {
    setOpenCategoryId((prev) => (prev === employedId ? null : employedId));
  };

  const handleApplyCategories = async (categories: string[]) => {
    try {
      await assignCategoryToWorker({
        worker_id: String(worker.id),
        category_id: categories,
      }).unwrap();

      const cached = employedsApi.endpoints.getCategoryEmployeds.select({ page: 1 })(
        store.getState(),
      );
      const loaded = cached.data?.data;
      const merged = mergeSelectedCategoriesForStore(
        categories,
        loaded,
        worker.categories,
      );
      dispatch(updateEmployedCategories({ employedId: worker.id, categories: merged }));
      refetch();
    } catch (error) {
      console.error('Error updating categories:', error);
    }
  };

  const iconCategory = () => {
    const hasCategories = worker?.categories?.length > 0;

    return (
      <div style={{ display: 'block', cursor: 'pointer', position: 'relative' }}>
        {!isMobile ? (
          <EmployedsСategory
            employedId={String(worker.id)}
            setIsCreatedModalOpen={setIsCreatedModalOpen}
            showIcon={!hasCategories}
            activeCategories={worker.categories.map((el) => el.id)}
            fallbackCategories={worker.categories}
            isOpen={openCategoryId === worker.id}
            onToggle={() => handleCategoryToggle(worker.id)}
          />
        ) : (
          <div onClick={() => handleCategoryToggle(worker.id)}>
            {!hasCategories ? (
              <div className={styles.addCategoryButton}>
                <Button theme='link'>
                  <IconPlus /> Добавить категорию
                </Button>
              </div>
            ) : (
              <div className={styles.buttonPencil}>
                <IconEdit />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.statusWrapper}>
      <div className={styles.statusBlock}>
        <span className={styles.label}>В приложении Скилла Работа</span>
        <WorkerAppStatusWithIcon
          workerApp={worker?.worker_app}
          positiveIcon={<IconDone />}
          negativeIcon={<IconClose />}
          className={styles.statusItem}
          isDeleted={worker?.is_deleted}
        />
      </div>
      <div className={styles.statusBlock}>
        <span className={styles.label}>Самозанятость в «Мой Налог»</span>
        <FnsStatusWithIcon
          workerApp={worker?.worker_app}
          positiveIcon={<IconDone />}
          negativeIcon={<IconClose />}
          className={styles.statusItem}
        />
      </div>
      <div className={styles.statusBlock}>
        <span className={styles.label}>Действующий договор</span>
        {worker?.is_in_inn_stoplist ? (
          <span className={styles.statusRed}>
            <IconClose /> Находится в списке бывших сотрудников
            {worker?.unified_contract?.status === 'signed_by_worker' ? (
              <Button onClick={() => setShowTerminateModal(true)} theme='danger' size='S'>
                <div className={styles.btnText}>
                  Расторгнуть <IconClose />
                </div>
              </Button>
            ) : null}
          </span>
        ) : hasContract ? (
          <span className={styles.statusGreen}>
            {statusIcons[worker?.unified_contract?.status as ContractStatus]}
            <span
              style={{
                color: statusColor[worker?.unified_contract?.status as ContractStatus],
              }}>
              {statusText[worker?.unified_contract?.status as ContractStatus]}
            </span>
          </span>
        ) : (
          <span className={styles.statusGreen}>
            <IconDone /> На каждый заказ создается отдельный договор
          </span>
        )}
      </div>
      {age < 18 ? (
        <div className={styles.statusBlock}>
          <span className={styles.label}>Возраст исполнителя</span>
          <span className={styles.statusItemSvg}>
            <IconAlert /> {reformattedAge}{' '}
            {`(${age} ${enumerate(age, ['год', 'года', 'лет'])})`} - несовершеннолетний
          </span>
        </div>
      ) : null}

      <div className={styles.categoryBlock}>
        <div className={styles.textCategory}>Категории {isMobile && iconCategory()}</div>
        <div
          style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
          }}>
          {worker?.categories?.length > 0 && (
            <div className={styles.categoriesRow}>
              <div className={styles.categoryes}>
                {worker?.categories?.map((category, index) => (
                  <TextWithTooltip key={index} content={category.name} />
                ))}
              </div>
              {!isMobile && iconCategory()}
            </div>
          )}
          {!isMobile && worker?.categories?.length === 0 && iconCategory()}
        </div>
      </div>

      <div className={styles.statusBlock}>
        <span className={styles.label}>Стоп-лист</span>
        <Switch
          color='#002CFB'
          styles={{
            label: {
              fontSize: '16px',
              lineHeight: '22px',
              color: '#121922',
              cursor: 'pointer',
            },
            track: {
              border: 'none',
              cursor: 'pointer',
            },
            thumb: {
              border: 'none',
            },
          }}
          checked={!!worker.is_in_stoplist}
          onChange={handleSwitchChange}
          disabled={pendingSwitch}
          label='Не отправлять уведомления'
        />
      </div>
      {worker.is_in_stoplist && (
        <div className={styles.comment}>
          <span>{worker.comment}</span>
          <div className={styles.buttonPencil}>
            <Button theme='link' onClick={() => setShowEditCommentModal(true)}>
              <IconEdit />
            </Button>
          </div>
        </div>
      )}
      <div className={styles.statusBlock} style={{ marginTop: '4px' }}>
        <span className={styles.createdAt}>
          Добавлен {moment(worker?.created_at).format('DD MMMM YYYY')}
        </span>
      </div>
      {showCommentModal && (
        <ModalComment
          setShowCommentModal={setShowCommentModal}
          onConfirm={handleBlockWithComment}
          currentComment={''}
          title='Ты уверен, что хочешь добавить исполнителя в Стоп-лист?'
          maxLength={100}
        />
      )}
      {showTerminateModal && (
        <ModalConfirm
          setShowLogoutModal={setShowTerminateModal}
          title='Расторгнуть договор?'
          text={
            isResident
              ? 'Будет создаваться отдельный договор на каждый заказ'
              : 'Ты не сможешь больше отправлять заказы исполнителю. Не забудь уведомить МВД в течение 3х дней'
          }
          confirmText='Расторгнуть'
          onConfirm={() => {
            confirmTerminateContract();
            refetch();
          }}
        />
      )}
      {showEditCommentModal && (
        <ModalComment
          setShowCommentModal={setShowEditCommentModal}
          onConfirm={handleEditComment}
          currentComment={worker.comment || ''}
          title='Ты уверен, что хочешь добавить исполнителя в Стоп-лист?'
          maxLength={100}
        />
      )}
      {isMobile && (
        <EmployedsCategoryDrawer
          opened={openCategoryId !== null}
          onClose={() => setOpenCategoryId(null)}
          selectedCategories={
            openCategoryId !== null ? worker.categories.map((el) => String(el.id)) : []
          }
          onApply={handleApplyCategories}
          setIsCreatedModalOpen={setIsCreatedModalOpen}
          employedId={String(worker.id)}
          fallbackCategories={worker.categories}
        />
      )}
    </div>
  );
};
